import { useMemo } from 'react';
import { CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { ProductInfoImage } from '../../ProductInfo/Image';
import { SimpleInputNumber } from 'views/Conversation/Sider/ToDoBundle/SimpleInputNumber';
import { Divider } from 'antd';
import { cx } from '@emotion/css';

interface RelatedSKUProps {
  visible?: boolean;
  canEdit?: boolean;
  data: Todo.RelatedSku[];
  storeId: string;
  disabled?: boolean;
  onQuantityChange?: (index: number, quantity: number) => void;
  showArrow?: boolean;
  platform?: General.PlatformUpperCase;
  onClear?: (e: React.MouseEvent) => void;
}

export const RelatedSKU = (props: RelatedSKUProps) => {
  const {
    visible,
    canEdit,
    data,
    storeId,
    disabled = false,
    onQuantityChange,
    showArrow = true,
    platform,
    onClear,
  } = props;

  const ids = data.map((item) => item.itemId).join(',');
  const { data: productData } = useQuery({
    ...queries.products.list({
      external_item_ids: ids,
      internal_store_id: storeId,
      page_no: 1,
      page_size: 999,
    }),
  });

  const productMap = useMemo(() => {
    const map = new Map<string, Conversation.Product>();
    productData?.itemList?.forEach((p) => map.set(p.externalItemId, p));
    return map;
  }, [productData]);

  return (
    <div
      className={cx(
        'group flex rounded p-3 items-center justify-between bg-[#F7F9FA]',
        canEdit && 'cursor-pointer',
      )}
    >
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        {data.map((sku, index) => {
          const product = productMap.get(sku.itemId);
          return (
            <div
              key={`${sku.itemId}___${sku.skuId}`}
              className="bg-[#F7F9FA] flex items-start gap-2 min-w-0 overflow-hidden"
            >
              <ProductInfoImage
                imgUrl={product?.imageUrl?.[0] ?? ''}
                itemName={(sku.itemName || product?.itemName) ?? ''}
                platform={platform}
              />
              <div className="min-w-0">
                <div className="text-[#232426] font-medium truncate">
                  {(sku.itemName || product?.itemName) ?? ''}
                </div>
                <div className="text-[#84868C] text-xs flex min-w-0 items-center gap-1">
                  {sku.skuName ? (
                    <>
                      <span className="truncate">{sku.skuName}</span>
                      <Divider type="vertical" className="!mx-1" />
                    </>
                  ) : null}
                  {sku.sellerSku ? <span>{sku.sellerSku}</span> : null}
                </div>
                <SimpleInputNumber
                  className="!inline-flex !bg-[#F2F3F5] mt-2"
                  min={1}
                  max={999}
                  disabled={disabled}
                  value={sku.quantity}
                  onChange={(val) => onQuantityChange?.(index, val)}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {canEdit && onClear && (
          <CloseCircleFilled
            onClick={onClear}
            className="!hidden group-hover:!inline-block !text-[#84868C] cursor-pointer hover:opacity-80"
          />
        )}
        {canEdit && showArrow && (
          <DownOutlined className={cx('ml-2 !text-xs !text-[#84868C]', visible && 'rotate-180')} />
        )}
      </div>
    </div>
  );
};
